import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {Registration} from "../../../../shared/interface/registration";
import {ReceivablesBalanceHistory} from "../../../../shared/interface/receivables-balance-history";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {RegistrationService} from "../../../../shared/services/registration.service";
import {ReceivablesBalanceService} from "../../../../shared/services/receivables-balance.service";
import {ReplaySubject} from "rxjs";
import {FilterService} from "../../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {ActivatedRoute, Router} from "@angular/router";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {AuthService} from "../../../../shared/services/auth.service";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
  selector: 'app-all-receivables-balance-history-list',
  templateUrl: './all-receivables-balance-history-list.component.html',
  styleUrls: ['./all-receivables-balance-history-list.component.scss']
})
export class AllReceivablesBalanceHistoryListComponent implements  AfterViewInit, OnDestroy {

  @Input() receivablesBalanceId?: string = null;
  @Input() embeddedView = false;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;

  userPermissions: ReplaySubject<UserPermissions>;
  receivablesBalanceHistory: ReceivablesBalanceHistory[] = [];
  displayedColumns: string[] = [ 'sum' , 'detail', 'createdAt'];
  totalData = 0;
  initialFilters: any;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private filterService: FilterService,
    private receivablesBalanceService: ReceivablesBalanceService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store<StoreRootState>,
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.receivablesBalanceService.getHistoryList()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ReceivablesBalanceHistory>) => {
          this.receivablesBalanceHistory = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      gte$sum: 'string',
      lte$sum: 'string',
      contains$description: 'string',
      contains$insuranceCompany: 'string',
      gte$createdAt: 'string',
      lte$createdAt: 'string',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {


        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            })
          ;
        } else {
          this.loadListData();
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      if (this.embeddedView) {
        this.loadListData();
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  async loadListData(): Promise<void> {
    this.receivablesBalanceService.loadHistoryList(this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters()
    );
  }
  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.receivablesBalanceId){
      filters.receivablesBalance = this.receivablesBalanceId;
    }

    return filters;
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      if (this.paginator.pageIndex === 0) {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: this.paginator.pageIndex,
          pageSize: this.paginator.pageSize,
          ...this.filtersForm.value
        })]);
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: 0,
          pageSize: this.paginator.pageSize,
          ...this.filtersForm.value,
        })]);
      }
    }
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.loadListData();
  }
  downloadReport(): void {
    this.receivablesBalanceService.downloadHistoryReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
}
